/**
 * NIGHTBOUND - a single gameplay action control.
 *
 * Big, tactile, and honest about its state: if the action is on cooldown the
 * seconds remaining are shown in text (never colour alone), and if it is
 * unavailable the reason is in the accessible name.
 */

import { cx } from './ui'

export type ActionTone = 'ember' | 'ash' | 'rot' | 'cold' | 'blood'

const TONES: Record<ActionTone, { border: string; glow: string; text: string; bg: string }> = {
  ember: {
    border: 'border-ember-600/60',
    glow: 'shadow-[0_0_22px_-8px_rgba(201,164,106,0.55)]',
    text: 'text-ember-400',
    bg: 'hover:bg-ember-500/10',
  },
  ash: {
    border: 'border-ash-600/60',
    glow: 'shadow-[0_0_22px_-10px_rgba(88,88,88,0.7)]',
    text: 'text-bone-300',
    bg: 'hover:bg-night-700/60',
  },
  rot: {
    border: 'border-rot-600/60',
    glow: 'shadow-[0_0_22px_-8px_rgba(111,122,74,0.5)]',
    text: 'text-rot-500',
    bg: 'hover:bg-rot-600/10',
  },
  cold: {
    border: 'border-cold-500/50',
    glow: 'shadow-[0_0_22px_-8px_rgba(143,155,179,0.45)]',
    text: 'text-cold-400',
    bg: 'hover:bg-cold-500/10',
  },
  blood: {
    border: 'border-blood-500/60',
    glow: 'shadow-[0_0_22px_-8px_rgba(168,69,47,0.55)]',
    text: 'text-blood-400',
    bg: 'hover:bg-blood-700/20',
  },
}

export interface ActionButtonProps {
  glyph: string
  label: string
  hint?: string
  onClick: () => void
  disabled?: boolean
  loading?: boolean
  /** Seconds until usable again; 0 when ready. */
  cooldownSeconds?: number
  /** Total cooldown, used to draw the ring. */
  cooldownTotal?: number
  tone?: ActionTone
  /** Larger, primary call to action. */
  emphasized?: boolean
  className?: string
}

export function ActionButton({
  glyph,
  label,
  hint,
  onClick,
  disabled = false,
  loading = false,
  cooldownSeconds = 0,
  cooldownTotal,
  tone = 'ash',
  emphasized = false,
  className,
}: ActionButtonProps) {
  const cooling = cooldownSeconds > 0
  const toneStyle = TONES[tone]
  const total = cooldownTotal ?? Math.max(cooldownSeconds, 1)
  const fraction = cooling ? Math.max(0, Math.min(1, cooldownSeconds / total)) : 0
  const circumference = 2 * Math.PI * 21

  const unavailable = disabled || cooling || loading
  const reason = loading
    ? 'Working'
    : cooling
      ? `${Math.ceil(cooldownSeconds)} seconds remaining`
      : disabled
        ? 'Unavailable right now'
        : undefined

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={unavailable}
      aria-label={reason ? `${label} - ${reason}` : label}
      className={cx(
        'group relative flex flex-col items-center justify-center gap-1 rounded-[4px] border bg-night-900/60 px-2 py-3 transition-all',
        emphasized ? 'min-h-[92px]' : 'min-h-[76px]',
        toneStyle.border,
        toneStyle.bg,
        !unavailable && toneStyle.glow,
        !unavailable && 'hover:-translate-y-0.5',
        unavailable && 'cursor-not-allowed opacity-55',
        className,
      )}
    >
      <span className="relative flex h-11 w-11 items-center justify-center">
        {cooling ? (
          <svg viewBox="0 0 44 44" className="absolute inset-0 h-full w-full -rotate-90">
            <circle cx="22" cy="22" r="21" fill="none" stroke="rgba(88,88,88,0.35)" strokeWidth="2" />
            <circle
              cx="22"
              cy="22"
              r="21"
              fill="none"
              stroke="currentColor"
              className={toneStyle.text}
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - fraction)}
            />
          </svg>
        ) : null}
        <span aria-hidden className={cx('text-xl', toneStyle.text)}>
          {glyph}
        </span>
        {cooling ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="nb-numeric rounded-full bg-night-950/80 px-1.5 py-0.5 text-[11px] text-bone-200">
              {Math.ceil(cooldownSeconds)}
            </span>
          </span>
        ) : null}
      </span>

      <span className={cx('nb-label text-[9.5px]', unavailable ? 'text-ash-400' : 'text-bone-300')}>
        {label}
      </span>
      {hint ? (
        <span className="nb-numeric text-[9px] text-ash-400">{hint}</span>
      ) : null}
    </button>
  )
}
